import React, { useState, useEffect, useRef } from 'react';
import { FaHashtag, FaAt } from 'react-icons/fa';
import { VscListSelection } from 'react-icons/vsc';
import { TbCircleArrowRight } from 'react-icons/tb';
import { CgCalendarDates } from 'react-icons/cg';

import JiraIssueModal from './JiraIssueModal';

const DynamicJiraIssueTable = ({ issues = [], openModal, closeModal }) => {
  const tableColumns = [
    'issueType',
    'issue_key',
    'summary',
    'status',
    'priority',
    'assignee',
    'epicName',
    'reporter',
    'duedate',
    'issue_id',
    'project_key',
    'project_id',
    'parent',
    'resolution',
    'storyPoints',
    'urgency',
    'automationStatus',
    'epicLink',
    'environment',
    'prediction',
    'businessValue',
    'resource_id',
  ];

  const getColumnTitle = (key) => {
    const titleMap = {
      issue_key: { title: 'Issue Key', icon: <FaHashtag /> },
      summary: { title: 'Summary', icon: <VscListSelection /> },
      issueType: { title: 'Issue Type', icon: '' },
      status: { title: 'Status', icon: <TbCircleArrowRight /> },
      priority: { title: 'Priority', icon: <FaHashtag /> },
      assignee: { title: 'Assignee', icon: <FaAt className="text-gray-500" /> },
      epicName: { title: 'Epic Name', icon: <FaHashtag /> },
      reporter: { title: 'Reporter', icon: <FaHashtag /> },
      duedate: {
        title: 'Due Date',
        icon: <CgCalendarDates className="text-gray-500" />,
      },
      issue_id: { title: 'Issue ID', icon: <FaHashtag /> },
      project_key: { title: 'Project Key', icon: <FaHashtag /> },
      project_id: { title: 'Project ID', icon: <FaHashtag /> },
      parent: { title: 'Parent', icon: <FaHashtag /> },
      resolution: { title: 'Resolution', icon: <FaHashtag /> },
      storyPoints: { title: 'Story Points', icon: <FaHashtag /> },
      urgency: { title: 'Urgency', icon: <FaHashtag /> },
      automationStatus: { title: 'Automation Status', icon: <FaHashtag /> },
      epicLink: { title: 'Epic Link', icon: <FaHashtag /> },
      environment: { title: 'Environment', icon: <FaHashtag /> },
      prediction: { title: 'Prediction', icon: <FaHashtag /> },
      businessValue: { title: 'Business Value', icon: <FaHashtag /> },
      resource_id: { title: 'Resource ID', icon: <FaHashtag /> },
    };
    return titleMap[key] || { title: key, icon: null };
  };

  const getTopFields = (issues) => {
    const fields = [];
    const keysRendered = [];

    for (let i = 0; i < tableColumns.length; i++) {
      const key = tableColumns[i];

      for (let j = 0; j < issues.length; j++) {
        if (issues[j][key]) {
          keysRendered.push(key);
          break;
        }
      }

      if (keysRendered.length === 5) break;
    }

    const firstIssue = issues[0] || {};
    for (let i = 0; i < keysRendered.length; i++) {
      fields.push(firstIssue[keysRendered[i]]);
    }

    return { fields, keysRendered };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short', // "Mon"
      year: 'numeric', // "2025"
      month: 'short', // "Feb"
      day: 'numeric', // "20"
    });
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-50 text-red-500';
      case 'High':
        return 'bg-orange-50 text-orange-500';
      case 'Medium':
        return 'bg-yellow-50 text-yellow-500';
      case 'Low':
        return 'bg-green-50 text-green-500';
      default:
        return 'bg-gray-50 text-gray-500';
    }
  };
  const { keysRendered } = getTopFields(issues);

  return (
    <>
      {/* DESKTOP View */}
      <table className="hidden min-w-full table-auto text-left text-sm md:block">
        <thead>
          <tr className="bg-zinc-100">
            {issues.length > 0 &&
              keysRendered.map((key, index) => {
                const { title, icon } = getColumnTitle(key);
                return (
                  <th
                    className="border border-gray-200 px-3 py-2 font-medium text-gray-700"
                    key={index}
                  >
                    <div className="flex items-center gap-2 ">
                      {icon}
                      {title}
                    </div>
                  </th>
                );
              })}
          </tr>
        </thead>
        <tbody>
          {issues.map((issue, index) => (
            <tr
              key={issue.id || index}
              className="cursor-pointer border-b border-gray-200 transition-colors duration-200 ease-in-out hover:bg-gray-50"
              onClick={() => openModal(issue)}
            >
              {keysRendered.map((key, i) => {
                const field = issue[key];

                if (key === 'issueType') {
                  return (
                    <td className="border border-gray-200 px-3 py-2" key={i}>
                      <div className="flex w-full justify-center">
                        <img
                          src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue.issueType?.toLowerCase()}.png`}
                          onError={(e) =>
                            (e.target.src =
                              '/assets/jiraissuetable/jiraIssueTypeIcons/default.png')
                          }
                          alt={`${issue.issueType} Icon`}
                          className="h-4 w-4"
                        />
                      </div>
                    </td>
                  );
                }

                if (key === 'status') {
                  return (
                    <td
                      className="whitespace-nowrap border border-gray-200 px-3 py-2"
                      key={i}
                    >
                      <span
                        className={`rounded-xl px-2 py-1 text-xs font-semibold ${
                          issue.status === 'To Do'
                            ? 'bg-blue-50 text-[#5652CF]'
                            : issue.status === 'In Progress'
                              ? 'bg-yellow-50 text-yellow-500'
                              : 'bg-green-50 text-green-500'
                        }`}
                      >
                        {issue.status}
                      </span>
                    </td>
                  );
                }

                if (key === 'priority') {
                  return (
                    <td
                      className="whitespace-nowrap border border-gray-200 px-3 py-2"
                      key={i}
                    >
                      <span
                        className={`rounded-xl px-2 py-1 text-xs font-semibold ${getPriorityStyles(issue.priority)}`}
                      >
                        {issue.priority}
                      </span>
                    </td>
                  );
                }

                if (key === 'duedate') {
                  return (
                    <td
                      className="border border-gray-200 px-3 py-2 text-gray-800"
                      key={i}
                    >
                      {field ? formatDate(field) : 'N/A'}
                    </td>
                  );
                }

                if (key === 'summary') {
                  return (
                    <td
                      className="border border-gray-200 px-3 py-2 text-gray-800"
                      key={i}
                    >
                      {field?.length > 50
                        ? `${field.substring(0, 50)}...`
                        : field}
                    </td>
                  );
                }

                return (
                  <td
                    className="whitespace-nowrap border border-gray-200 px-3 py-2 text-gray-800"
                    key={i}
                  >
                    {field ?? '--'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile View */}
      <div className="sh space-y-2 bg-zinc-700 p-2 md:hidden">
        {issues.map((issue, index) => (
          // eslint-disable-next-line
          <div
            key={issue.id || index}
            className="rounded-lg border border-gray-200 bg-white p-3 shadow"
            onClick={() => openModal(issue)}
          >
            {/* Top row: Icon + Issue Key + Status + Priority */}
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {keysRendered.includes('issueType') && (
                  <img
                    src={`/assets/jiraissuetable/jiraIssueTypeIcons/${issue.issueType?.toLowerCase()}.png`}
                    onError={(e) =>
                      (e.target.src =
                        '/assets/jiraissuetable/jiraIssueTypeIcons/default.png')
                    }
                    alt={`${issue.issueType} Icon`}
                    className="h-4 w-4"
                  />
                )}
                {keysRendered.includes('issue_key') && (
                  <span className="text-sm font-semibold text-gray-800">
                    {issue.issue_key}
                  </span>
                )}
              </div>
              <div className="flex space-x-2">
                {keysRendered.includes('status') && (
                  <span
                    className={`rounded-xl px-2 py-1 text-xs font-semibold ${
                      issue.status === 'To Do'
                        ? 'bg-blue-50 text-[#5652CF]'
                        : issue.status === 'In Progress'
                          ? 'bg-yellow-50 text-yellow-500'
                          : 'bg-green-50 text-green-500'
                    }`}
                  >
                    {issue.status}
                  </span>
                )}
                {keysRendered.includes('priority') && (
                  <span
                    className={`rounded-xl px-2 py-1 text-xs font-semibold ${getPriorityStyles(issue.priority)}`}
                  >
                    {issue.priority}
                  </span>
                )}
              </div>
            </div>
            {keysRendered.includes('summary') && (
              <div className="mb-2 text-sm text-gray-800">
                {issue.summary?.length > 80
                  ? `${issue.summary.slice(0, 80)}...`
                  : issue.summary}
              </div>
            )}

            {/* Other fields in card body */}
            <div className="space-y-1">
              {keysRendered.map((key) => {
                if (
                  [
                    'issueType',
                    'issue_key',
                    'status',
                    'priority',
                    'summary',
                  ].includes(key)
                )
                  return null; // Already rendered above

                const field = issue[key];
                const { title } = getColumnTitle(key);

                return (
                  <div key={key} className="text-sm text-gray-700">
                    <strong>{title}:</strong>{' '}
                    {key === 'duedate'
                      ? field
                        ? formatDate(field)
                        : 'N/A'
                      : typeof field === 'string' && field.length > 80
                        ? `${field.slice(0, 80)}...`
                        : (field ?? '--')}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
    // </div>
  );
};

// export default DynamicJiraIssueTable;
const DynamicJiraIssueTableWrapper = ({ issues = [] }) => {
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Rename isHovered to isScaled for clarity.
  const [isScaled, setIsScaled] = useState(false);
  const scaledRef = useRef(null);

  const openModal = (issue) => {
    setSelectedIssue(issue);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedIssue(null);
    setIsModalOpen(false);
  };

  // Attach a click listener to detect clicks outside the scaled component.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scaledRef.current && !scaledRef.current.contains(event.target)) {
        setIsScaled(false);
      }
    };

    if (isScaled) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isScaled]);

  return (
    <div className="flex w-full items-center justify-start gap-2 pt-0">
      <div className="hidden w-10 sm:block"></div>
      <div className="mb-4 flex w-full items-center justify-center overflow-clip rounded-xl bg-white max-md:max-h-[60vh] sm:w-[88%] md:max-h-[300px]">
        {/* eslint-disable-next-line */}
        <div
          onClick={() => setIsScaled(true)}
          className={`scrollbar-none w-full transform cursor-pointer items-center justify-center overflow-x-auto overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-md 
        transition-all duration-300 ease-in-out max-md:max-h-[60vh] md:max-h-[300px]
        ${isScaled ? 'opacity-0' : 'opacity-100'}`}
        >
          <div className="pointer-events-none">
            <DynamicJiraIssueTable
              issues={issues}
              openModal={openModal}
              closeModal={closeModal}
            />
          </div>
        </div>

        {isScaled && (
          <div className="duration-600 fixed inset-0 z-50 flex transform items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm transition-all ease-in-out max-sm:p-3 sm:mr-10 ">
            <div
              ref={scaledRef}
              className="max-h-[600px] w-full items-center justify-center overflow-hidden rounded-xl bg-white sm:w-[60%] "
            >
              <div className="scrollbar-none max-h-[600px] items-center justify-center overflow-x-auto overflow-y-auto rounded-xl border border-gray-300 bg-white shadow-md transition-all duration-300 ease-in-out">
                <DynamicJiraIssueTable
                  issues={issues}
                  openModal={openModal}
                  closeModal={closeModal}
                />
              </div>
            </div>
          </div>
        )}

        {isModalOpen && (
          <JiraIssueModal
            issue_key={selectedIssue.issue_key}
            onClose={closeModal}
          />
        )}
      </div>
    </div>
  );
};

export default DynamicJiraIssueTableWrapper;
